'use client';

import React, { useMemo } from 'react';
import styled from 'styled-components';
import { mqLargeMin } from 'styled/tools/media-queries';
import type { TestingStandardsModule } from 'lib/pdp-modules/types';

const TestingWrapper = styled.div`
  padding: 3.5rem 1rem 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2rem;
  background-color: var(--fill-tertiary);

  @media ${mqLargeMin} {
    padding: 5rem;
    flex-direction: row;
  }
`;

const TestingCardWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.75rem;

  @media ${mqLargeMin} {
    flex-direction: row;
  }
`;

const TestingTitle = styled.h2`
  font-size: 2rem;
  font-weight: 350;
  line-height: 110%;
  letter-spacing: -0.64px;

  @media ${mqLargeMin} {
    font-size: 3rem;
  }
`;

const TestingContent = styled.div`
  max-width: 31rem;

  * {
    color: var(--text-primary);
  }
`;

const TestingCardImage = styled.img`
  max-width: 2rem;

  @media ${mqLargeMin} {
    max-width: unset;
  }
`;

const TestingContentDescription = styled.p`
  margin-top: 1.5rem;
  font-weight: 350;
  line-height: 140%;
  letter-spacing: -0.16px;
`;

const TestingCard = styled.div`
  border-radius: 1rem;
  padding: 1.5rem 1rem 1rem;
  flex: 1 0 0;
  display: flex;
  align-items: center;
  gap: 1rem;
  background-color: var(--scrim-glass-dark-08);

  @media ${mqLargeMin} {
    max-width: 16rem;
    width: 100%;
    height: 11rem;
    flex-direction: column;
    text-align: center;
    justify-content: center;
    background-color: var(--fill-secondary);
  }
`;

const TestingCardContent = styled.p`
  font-size: 14px;
  font-weight: 350;
  line-height: 140%;
  letter-spacing: -0.07px;
  color: var(--text-primary);
`;

type ProductTestingProps = {
  testingModule: TestingStandardsModule;
  productInfo: { shortTitle: string };
};

export default function ProductTesting({
  testingModule,
  productInfo,
}: ProductTestingProps) {
  const moduleContent = useMemo(() => {
    const baseContent = testingModule.content || '';
    const content = baseContent.replace(
      'PRODUCT-TITLE',
      productInfo.shortTitle,
    );

    const cardInfo = (testingModule.cards ?? [])
      .map((card) => ({
        cardContent: card.text,
        cardMedia: card.icon,
      }))
      // ensure we don't display any cards with no content or icon set
      .filter((cardItem) => cardItem.cardContent && cardItem.cardMedia);

    return {
      content,
      cardInfo,
    };
  }, [testingModule]);

  return (
    <TestingWrapper>
      <TestingContent>
        <TestingTitle>{testingModule.title}</TestingTitle>
        <TestingContentDescription>
          {moduleContent.content}
        </TestingContentDescription>
      </TestingContent>
      <TestingCardWrapper>
        {moduleContent.cardInfo.map((card) => (
          <TestingCard key={`testing-card-${card.cardContent}`}>
            <TestingCardImage
              src={card.cardMedia?.url}
              alt={card.cardMedia?.alt}
            />
            <TestingCardContent>{card.cardContent}</TestingCardContent>
          </TestingCard>
        ))}
      </TestingCardWrapper>
    </TestingWrapper>
  );
}
